import { useClient } from '../supabase/client'

import { useMutation, useQueryClient } from '@tanstack/vue-query'
import type { Database, MutationOptions } from '@xleddyl/types'

export type QueryRoot =
   | 'abilitation-grid'
   | 'accessible-organizations'
   | 'adminlog'
   | 'catchrule'
   | 'country'
   | 'cultivation-plan'
   | 'cultivation-plan-document'
   | 'cultivation-plan-overview'
   | 'cultivation-plan-permit'
   | 'cultivation-plan-remark'
   | 'cultivation-plan-restriction'
   | 'customer'
   | 'customer-organization'
   | 'dashboard-rbac'
   | 'date'
   | 'document'
   | 'fiscal-report'
   | 'inspector'
   | 'inspector-grid'
   | 'localization-grid'
   | 'location'
   | 'location-availability'
   | 'location-by-water-number'
   | 'location-by-water-number-grid'
   | 'location-grid'
   | 'location-map'
   | 'location-name'
   | 'location-poi'
   | 'location-specie'
   | 'management-section'
   | 'management-section-holder'
   | 'management-subsection'
   | 'managementsection'
   | 'ordinance-grid'
   | 'organization'
   | 'organization-group'
   | 'organization-notification'
   | 'organization-signature'
   | 'organizationgroup-grid'
   | 'organizationpoints-grid'
   | 'permit'
   | 'permit-grid'
   | 'permit-info'
   | 'poi'
   | 'price'
   | 'product'
   | 'product-grid'
   | 'product-location'
   | 'promocode'
   | 'promocode-grid'
   | 'region'
   | 'rolematrix'
   | 'specie'
   | 'specie-grid'
   | 'speciegroup-grid'
   | 'stats-accesses'
   | 'stats-catches'
   | 'stats-report'
   | 'stats-sales'
   | 'stocking'
   | 'supervision-zone'
   | 'user'
   | 'user-grid'
   | 'user-organization'
   | 'waternumber-specie'

export type InvalidationKey = readonly [QueryRoot, ...unknown[]]

type DashboardFunctions = Database['dashboard']['Functions']
type DashboardFunctionName = keyof DashboardFunctions
type DashboardFunctionArgs<F extends DashboardFunctionName> = DashboardFunctions[F]['Args']
type DashboardFunctionReturns<F extends DashboardFunctionName> = DashboardFunctions[F]['Returns']

type RpcMutationConfig<F extends DashboardFunctionName, P> = {
   fn: F
   args?: (props: P) => DashboardFunctionArgs<F>
   successKey?: string | ((vars: P) => string)
   successParams?: (vars: P) => Record<string, unknown>
   invalidates?: (props: P) => InvalidationKey[]
}

export const createRpcMutation = <F extends DashboardFunctionName, P = DashboardFunctionArgs<F>>(
   config: RpcMutationConfig<F, P>
) => {
   return (options?: MutationOptions) => {
      const client = useClient()
      const notification = useNotification()
      const qc = useQueryClient()

      const { t } = useI18n()

      return useMutation({
         onSuccess: (_, variables) => {
            if (!config.successKey) return

            const key =
               typeof config.successKey === 'function'
                  ? config.successKey(variables)
                  : config.successKey

            notification?.add({
               type: 'success',
               title: t('info.success'),
               description: config.successParams ? t(key, config.successParams(variables)) : t(key),
            })
         },
         onMutate: async () => {
            await qc.getDefaultOptions().mutations?.onMutate?.(options)
         },
         mutationFn: async (props: P) => {
            const args = config.args
               ? config.args(props)
               : (props as unknown as DashboardFunctionArgs<F>)

            const { data } = await client.schema('dashboard').rpc(config.fn, args).throwOnError()

            const keys = config.invalidates?.(props) ?? []
            await Promise.all(keys.map((queryKey) => qc.refetchQueries({ queryKey })))

            return data as DashboardFunctionReturns<F>
         },
      })
   }
}
